import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TEventInputData } from '@/core/constants/event_types/additional_types';
import { ShopConfigDataHandler } from '@/core/data_handlers/shop_config_data';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { SHA256 } from '@/core/event_logic/utils/hash_utils';
import { EActions } from '@/core/types';
import { Logger } from '@/shared/logger/logger';

import { handleConsent } from '../actions/consent';
import { handleEvent } from '../actions/event';
import { handleInit } from '../actions/init';
import { DataLayerStateHandler } from './state';

export enum PersistentEventState {
  // completed
  COMPLETED = 'completed',
  // In queue, before processing
  PENDING = 'pending',
  // Being processed
  PROCESSING = 'processing',
}

type EventArgs = [EActions, ...any[]];

type PersistentEvent = {
  args: EventArgs;
  createdOn: number;
  finishedDestinations: string[]; // combination of event-destinations that are processed, only relevant for `handleEvent`
  lastUpdatedOn: number;
  retryCount: number;
  state: PersistentEventState;
};

enum EventResult {
  FAILED = 'failed',
  SUCCESS = 'success',
  // data layer was not ready
  UNPROCESSED = 'not-processed',
}

type PersistentEventRecord = Record<string, PersistentEvent>;
type Timer = ReturnType<typeof setTimeout>;
export type DataLayerListener = (...args: any[]) => Promise<{
  data?: any;
  success: boolean;
}>;

// Retry events after 5 seconds of no updates
// export const STALE_RETRY_THRESHOLD = 5000;
// Event queue processing retry interval
const RETRY_INTERVAL_MS = 100;
// expire events after 3 retries
const MAX_RETRY_LIMIT = 3;
// retain 100 keys in the storage at max.
const QUEUE_MAX_KEYS = 100;

export class PersistentDataLayer {
  events: PersistentEventRecord;
  // tracks the events pushed into the data layer
  storage = new StorageHandler<PersistentEventRecord>(
    `${Logger.bundleName}_wtag_persistent_data_layer`,
    {},
    EBrowserStorageType.LOCALSTORAGE,
  );
  retryTimer: Timer | null;
  retryEnabled: boolean;
  listeners: Record<EActions, DataLayerListener> = {
    [EActions.Consent]: handleConsent,
    [EActions.Event]: handleEvent,
    [EActions.Init]: handleInit,
  };

  constructor() {
    this.events = this.storage.get();
    this.retryTimer = null;
    const shopConfig = ShopConfigDataHandler.getCurrentConfig();
    this.retryEnabled =
      shopConfig.config.script_tag_wtag_retry_enabled ?? false;

    Logger.info('Persistent data layer initialized', {
      retryEnabled: this.retryEnabled,
    });
    if (!this.retryEnabled) return;
    // process older events if pending

    const prevState = this.storage.get();
    Object.keys(prevState)
      .sort(
        (a, b) =>
          (prevState[b]?.createdOn || 0) - (prevState[a]?.createdOn || 0),
      )
      .slice(0, QUEUE_MAX_KEYS)
      .forEach(key => {
        if (prevState[key]) this.events[key] = prevState[key];
      });

    void this.processAllTasks();
  }

  private saveQueue() {
    if (!this.retryEnabled) return;
    this.storage.set(this.events);
  }

  public async push(...inputEvents: EventArgs[]) {
    const now = Date.now();

    await Promise.all(
      inputEvents.map(async (args, index) => {
        const id = await this.generateTaskId(args);
        if (this.events[id])
          // event already exists
          return;
        if (args[0] === EActions.Event) {
          const event = args[2] as TEventInputData;
          // Add metadata to the event
          event.metadata = event.metadata ?? { retryInfo: [] };
          event.metadata.retryInfo = event.metadata.retryInfo ?? [];
          event.metadata.retryInfo.push({
            layer: 'datalayer',
            reason: `entered datalayer`,
            timestamp: Date.now(),
          });
          args[2] = event;
        }
        this.events[id] = {
          args,
          createdOn: Date.now(),
          finishedDestinations: [],
          lastUpdatedOn: now + index,
          retryCount: 0,
          state: PersistentEventState.PENDING,
        };
        void this.processTask(id);
      }),
    );
    this.saveQueue();
  }

  private async generateTaskId(args: any[]) {
    const payloadStr = JSON.stringify(args);
    return SHA256(payloadStr);
  }

  private async processTask(taskId: string, retried = false) {
    const task = this.events[taskId];
    if (!task) {
      Logger.warn('[datalayer] Task not found', { taskId });
      return;
    }

    if (task.state !== PersistentEventState.PENDING && !retried) return;

    task.state = PersistentEventState.PROCESSING;
    task.lastUpdatedOn = Date.now();
    // this line of function takes control never leaves the event.

    const { args, finishedDestinations, retryCount } = task;
    const taskType = args[0];

    if (retryCount >= MAX_RETRY_LIMIT) {
      Logger.info('[datalayer] Event expired after max retires', {
        args,
        taskId,
      });
      delete this.events[taskId];
      this.saveQueue();
      return;
    }

    // If it is not an array eject it
    if (!Array.isArray(args)) {
      Logger.info('[datalayer] Not a valid event', task);
      delete this.events[taskId];
      return;
    }

    const [result, data] =
      taskType === EActions.Event
        ? await this._triggerAction([...args, finishedDestinations])
        : await this._triggerAction(args);

    if (result === EventResult.UNPROCESSED) {
      setTimeout(() => this.processTask(taskId, true), RETRY_INTERVAL_MS);
      return;
    }

    if (!this.retryEnabled) {
      delete this.events[taskId];
      return;
    }

    switch (result) {
      case EventResult.SUCCESS: {
        if (args[0] === EActions.Event) {
          task.state = PersistentEventState.COMPLETED;
          this.addRetryEntryToEvent(
            'Successfully sent to all destinations',
            args,
          );
        } else delete this.events[taskId];
        break;
      }
      case EventResult.FAILED: {
        if (args[0] === EActions.Event && data)
          task.finishedDestinations = task.finishedDestinations.concat(
            data as string[],
          );
        task.retryCount++;
        this.addRetryEntryToEvent(
          `Failed to send to all destinations, received: ${JSON.stringify(data)}`,
          args,
        );
        setTimeout(() => this.processTask(taskId, true), RETRY_INTERVAL_MS);
        break;
      }
    }

    this.saveQueue();
  }

  private addRetryEntryToEvent(message: string, args: EventArgs) {
    if (args[0] !== EActions.Event) return;

    const event = args[2] as TEventInputData;
    event.metadata = event.metadata ?? { retryInfo: [] };
    event.metadata.retryInfo = event.metadata.retryInfo ?? [];
    event.metadata.retryInfo.push({
      layer: 'datalayer',
      reason: message,
      timestamp: Date.now(),
    });
  }

  // Returns: [result, data]
  private async _triggerAction(args: any[]): Promise<[EventResult, any]> {
    const action = args[0] as EActions;
    const params = args.slice(1);
    if (!DataLayerStateHandler.isActionPermissible(action)) {
      return [EventResult.UNPROCESSED, null];
    }

    const listener = this.listeners[action];
    if (!listener) {
      Logger.warn(`No listener registered for action ${action}`);
      return [EventResult.FAILED, null];
    }

    try {
      const result = await listener(...params);
      Logger.info('[DataLayer] Triggered Action result:', action, result);
      const resultState = result.success
        ? EventResult.SUCCESS
        : EventResult.FAILED;
      return [resultState, result.data];
    } catch (error) {
      Logger.error(`Error in Triggering Action ${action}`, error);
      return [EventResult.FAILED, null];
    }
  }

  private async processAllTasks() {
    const tasks = Object.keys(this.events).map(taskId =>
      this.processTask(taskId),
    );
    await Promise.all(tasks);
  }

  /**
   * This is a Special function that is called after initialization to process the complete datalayer
   * This returns a 1:1 copy of Actions added in the datalayer with whether the events are processed or not
   */
  public async processCompleteDataLayer() {
    await this.processAllTasks();
  }

  // Only for testing purposes
  setListener(action: EActions, listener: DataLayerListener): void {
    this.listeners[action] = listener;
  }

  size() {
    return Object.keys(this.events).length || 0;
  }

  get eventValues() {
    // get sorted events based on insertion order
    return Object.values(this.events).sort(
      (a, b) => a.lastUpdatedOn - b.lastUpdatedOn,
    );
  }

  clear() {
    this.events = {};
    this.storage.set({});
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}
